// Per react-conventions.md: frontend always renders user-facing datetimes
// in Asia/Singapore. Leaving timeZone unset (browser-local) breaks for
// non-SGT viewers; pinning to 'UTC' (as this file used to) wall-clock
// drifts by +8h for SGT users.
const TZ = 'Asia/Singapore';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const dateFormatter = new Intl.DateTimeFormat('en-SG', {
  day: '2-digit',
  month: 'numeric',
  year: 'numeric',
  weekday: 'short',
  timeZone: TZ,
});

const shortDateFormatter = new Intl.DateTimeFormat('en-SG', {
  day: '2-digit',
  month: 'numeric',
  year: 'numeric',
  timeZone: TZ,
});

const timeFormatter = new Intl.DateTimeFormat('en-SG', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: TZ,
});

const currencyFormatter = new Intl.NumberFormat('en-SG', {
  style: 'currency',
  currency: 'SGD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('en-SG', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const relativeTimeFormatter = new Intl.RelativeTimeFormat('en-SG', { numeric: 'auto' });

/**
 * "4 minutes ago" — computed once at render, not ticking. Used where the exact
 * clock time matters less than how long ago it was; falls back to the short
 * date past a week, where "12 days ago" is harder to place than the date.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const magnitude = Math.abs(seconds);

  if (magnitude < 45) return 'just now';
  if (magnitude < 3600) return relativeTimeFormatter.format(Math.round(seconds / 60), 'minute');
  if (magnitude < 86_400) return relativeTimeFormatter.format(Math.round(seconds / 3600), 'hour');
  if (magnitude < 604_800) return relativeTimeFormatter.format(Math.round(seconds / 86_400), 'day');
  return formatShortDate(iso);
}

/** "26 Dec 2025 (Fri)" */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parts = dateFormatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${MONTH_ABBR[Number(get('month')) - 1]} ${get('year')} (${get('weekday')})`;
}

/** "26 Dec 2025" — short form without day-of-week. Use in headers, footers, and chrome text where DOW is noise. */
export function formatShortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parts = shortDateFormatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${MONTH_ABBR[Number(get('month')) - 1]} ${get('year')}`;
}

export function formatMonthDay(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return isoDate;
  const month = Number(match[2]);
  const day = Number(match[3]);
  return `${day} ${MONTH_ABBR[month - 1]}`;
}

const paddedTimeFormatter = new Intl.DateTimeFormat('en-SG', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: TZ,
});

/** "04 Dec 2025 @07:00AM" — zero-padded hour per the events-list chip design */
export function formatDateAtTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const time = paddedTimeFormatter.format(date).replace(/\s/g, '').toUpperCase();
  return `${formatShortDate(iso)} @${time}`;
}

/** "8:00AM", "6:30AM", "10:30AM" — accepts ISO datetimes or plain "HH:MM" strings */
export function formatTime(value: string): string {
  // ISO datetime (contains "T")
  const asDate = new Date(value);
  if (!Number.isNaN(asDate.getTime()) && value.includes('T')) {
    return timeFormatter.format(asDate).replace(/\s/g, '').toUpperCase();
  }
  // Plain HH:MM
  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const period = hours < 12 ? 'AM' : 'PM';
    return `${displayHour}:${String(minutes).padStart(2, '0')}${period}`;
  }
  return value;
}

/** "07 Sep 2026, 9:45PM" — short date and unpadded time in SGT */
export function formatShortDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatShortDate(iso)}, ${formatTime(iso)}`;
}

export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return '—';
  return currencyFormatter.format(amount);
}

/** Formats a percentage value where the input is the percent itself (10 → "10%"). */
export function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return percentFormatter.format(value / 100);
}

/**
 * Smart date range formatter that compresses shared parts:
 *   Same month:  "20–23 Jan 2026"
 *   Same year:   "20 Jan – 23 Feb 2026"
 *   Diff year:   "20 Dec 2025 – 23 Feb 2026"
 *   Single date: "20 Jan 2026"
 */
export function formatDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return '\u2014';

  const parse = (iso: string) => {
    const parts = shortDateFormatter.formatToParts(new Date(iso));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return { day: get('day'), month: MONTH_ABBR[Number(get('month')) - 1], year: get('year') };
  };

  if (!start || !end) {
    const p = parse((start ?? end)!);
    return `${p.day} ${p.month} ${p.year}`;
  }

  const s = parse(start);
  const e = parse(end);

  if (s.year === e.year && s.month === e.month) {
    return `${s.day}\u2013${e.day} ${e.month} ${e.year}`;
  }
  if (s.year === e.year) {
    return `${s.day} ${s.month} \u2013 ${e.day} ${e.month} ${e.year}`;
  }
  return `${s.day} ${s.month} ${s.year} \u2013 ${e.day} ${e.month} ${e.year}`;
}

const singaporeDatePartsFormatter = new Intl.DateTimeFormat('en-SG', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: TZ,
});

function singaporeDateParts(date: Date): { day: string; month: string; year: string } {
  const parts = singaporeDatePartsFormatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { day: get('day'), month: get('month'), year: get('year') };
}

/**
 * "2025-12-26" -- the Singapore calendar day. `formatDateParam` answers with the
 * *browser's* day, a different date for any instant between 16:00Z and midnight.
 */
export function formatSingaporeDateParam(date: Date): string {
  const { day, month, year } = singaporeDateParts(date);
  return `${year}-${month}-${day}`;
}

/**
 * "9 Aug 2026" -- matches Ruby's `%-d %b %Y` character for character, so the
 * same copy reads identically either side. `formatShortDate` pads the day;
 * Intl's own month names give "Sept".
 */
export function formatUnpaddedShortDate(date: Date): string {
  const { day, month, year } = singaporeDateParts(date);
  return `${Number(day)} ${MONTH_ABBR[Number(month) - 1]} ${year}`;
}

/** "2025-12-26" -- timezone-safe YYYY-MM-DD for API params */
export function formatDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Inverse of `formatDateParam`. Built from the numeric fields because
 * `new Date("2025-12-26")` parses as UTC and lands on the previous day in a
 * negative-UTC browser.
 */
export function parseDateParam(raw: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return undefined;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/**
 * Today's Singapore calendar day as a local-midnight Date, for pickers that
 * default to "today" regardless of where the browser sits.
 */
export function todaySingaporeDate(now: Date = new Date()): Date {
  return parseDateParam(formatSingaporeDateParam(now))!;
}

/** Build ordered tag parts: diet → chef's pick → nutrition → allergens. */
export function formatTagParts(
  dietTags: string[],
  chefRecommended: boolean,
  nutritionTags: string[],
  allergenTags: string[],
  chefPickLabel: string,
): string[] {
  return [
    ...dietTags,
    ...(chefRecommended ? [chefPickLabel] : []),
    ...nutritionTags.map((t) => t.replace(/_/g, ' ')),
    ...allergenTags,
  ];
}

/**
 * The API counts searched-list totals up to SEARCH_COUNT_CAP only (it counts
 * at most cap + 1 rows — GRAIN-2573), so a searched total above the cap is a
 * sentinel, not an exact number: render it as "1,000+". Browse (unsearched)
 * totals are exact and render as plain numbers.
 */
export const SEARCH_COUNT_CAP = 1000;

export function formatSearchTotal(total: number, searchActive: boolean): string {
  if (searchActive && total > SEARCH_COUNT_CAP) {
    return `${SEARCH_COUNT_CAP.toLocaleString()}+`;
  }
  return total.toLocaleString();
}
